import React from 'react';
import type { CodeLabel } from './outcome';
import { Btn, LABEL_CLASS, SalesDialog } from './ui';

interface Props {
  open: boolean;
  subtitle?: string;
  reasons: CodeLabel[];
  reason: string;
  on_reason: (code: string) => void;
  note: string;
  on_note: (note: string) => void;
  on_back: () => void;
  on_confirm: () => void;
  busy?: boolean;
  error?: string;
}

// Skip is terminal: a reason is required, the note is optional, confirming moves to the next task.
function SkipDialog({
  open,
  subtitle,
  reasons,
  reason,
  on_reason,
  note,
  on_note,
  on_back,
  on_confirm,
  busy = false,
  error = ''
}: Props) {
  return (
    <SalesDialog
      open={open}
      onClose={on_back}
      title="Skip this task"
      subtitle={subtitle}
      testId="skip-dialog"
      footer={
        <>
          <Btn variant="neutral" onClick={on_back} disabled={busy}>
            Back
          </Btn>
          <span title={reason ? undefined : 'Choose a skip reason first'}>
            <Btn variant="primary" onClick={on_confirm} disabled={busy || !reason}>
              Skip and next
            </Btn>
          </span>
        </>
      }
    >
      <div className="flex flex-col gap-3.5 px-4 py-3.5">
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className={`mb-1.5 p-0 ${LABEL_CLASS}`}>Reason (required)</legend>
          {reasons.length === 0 ? (
            <div className="text-xs text-content-muted">No skip reasons are configured.</div>
          ) : null}
          {reasons.map((r) => {
            const on = reason === r.code;
            return (
              <label
                key={r.code}
                htmlFor={`sales-task-skip-${r.code}`}
                className="flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-3 text-[13px] max-sm:min-h-[44px]"
                style={{
                  border: `1px solid ${on ? 'var(--brand-primary)' : 'var(--border-subtle)'}`,
                  background: on ? 'var(--brand-primary-soft)' : 'var(--surface-card)',
                  color: 'var(--text-primary)'
                }}
              >
                <input
                  id={`sales-task-skip-${r.code}`}
                  type="radio"
                  name="sales-task-skip-reason"
                  value={r.code}
                  checked={on}
                  disabled={busy}
                  onChange={() => on_reason(r.code)}
                  // The CMS's global input rule forces 46px with !important.
                  className="!h-4 !w-4 shrink-0"
                  style={{ margin: 0, accentColor: 'var(--brand-primary)' }}
                />
                {r.label}
              </label>
            );
          })}
        </fieldset>
        <label htmlFor="sales-task-skip-note" className="block text-xs text-content-muted">
          Note (optional)
          <textarea
            id="sales-task-skip-note"
            aria-label="Skip note"
            value={note}
            disabled={busy}
            maxLength={255}
            rows={3}
            onChange={(e) => on_note(e.target.value)}
            className="mt-1 block w-full rounded-lg px-2.5 py-2 text-[12.5px]"
            style={{
              border: '1px solid var(--input-border)',
              background: 'var(--input-bg)',
              color: 'var(--input-text)',
              resize: 'vertical'
            }}
          />
        </label>
        {error ? (
          <div role="alert" className="text-xs" style={{ color: 'var(--state-error-text)' }}>
            {error}
          </div>
        ) : null}
      </div>
    </SalesDialog>
  );
}

export default SkipDialog;
